import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  FileSignature, Plus, Search, Clock, CheckCircle2, XCircle, Ban, AlertTriangle, Archive, ArchiveRestore
} from 'lucide-react'
import { api, formatMoney, formatDate } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'

interface Orcamento {
  id: number
  numero: number
  tipo: 'venda' | 'servico'
  status: string
  cliente_nome: string | null
  vendedor_nome: string | null
  tecnico_nome: string | null
  valido_ate: string
  expirado: boolean
  dias_restantes: number
  total: number
  arquivado: boolean
  convertido_pedido_id: number | null
  convertido_os_id: number | null
  criado_em: string
}

const STATUS_META: Record<string, { label: string; cor: string; icon: any }> = {
  aberto: { label: 'Aberto', cor: 'bg-sky-100 text-sky-800', icon: Clock },
  aceito: { label: 'Aceito', cor: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  recusado: { label: 'Recusado', cor: 'bg-amber-100 text-amber-800', icon: XCircle },
  cancelado: { label: 'Cancelado', cor: 'bg-rose-100 text-rose-700', icon: Ban },
}

/** Selo de validade: o que o operador precisa saber de relance. */
function SeloValidade({ o }: { o: Orcamento }) {
  if (o.status !== 'aberto') return <span className="text-ink-400">—</span>
  if (o.expirado) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-rose-700 font-medium">
        <AlertTriangle size={12} /> Vencido em {formatDate(o.valido_ate)}
      </span>
    )
  }
  const urgente = o.dias_restantes <= 2
  return (
    <span className={`text-xs ${urgente ? 'text-amber-700 font-medium' : 'text-ink-600'}`}>
      {o.dias_restantes === 0 ? 'Vence hoje'
        : o.dias_restantes === 1 ? 'Vence amanhã'
        : `${o.dias_restantes} dias`}
    </span>
  )
}

export default function OrcamentosPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState('aberto')
  const [tipo, setTipo] = useState('')
  const [busca, setBusca] = useState('')
  const [verArquivados, setVerArquivados] = useState(false)

  const { data: orcamentos = [], isLoading } = useQuery<Orcamento[]>({
    queryKey: ['orcamentos', status, tipo, verArquivados],
    queryFn: async () => (await api.get('/orcamentos', {
      params: { status: status || undefined, tipo: tipo || undefined,
                arquivados: verArquivados },
    })).data,
  })

  const filtrados = useMemo(() => {
    if (!busca.trim()) return orcamentos
    const t = busca.toLowerCase()
    return orcamentos.filter(o =>
      (o.cliente_nome || '').toLowerCase().includes(t) || String(o.numero).includes(t))
  }, [orcamentos, busca])

  const totalAberto = useMemo(
    () => orcamentos.filter(o => o.status === 'aberto' && !o.expirado)
                    .reduce((s, o) => s + Number(o.total || 0), 0),
    [orcamentos])
  const vencidos = orcamentos.filter(o => o.status === 'aberto' && o.expirado).length

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileSignature className="text-brand-600" /> Orçamentos
          </h1>
          <p className="text-ink-600 text-sm">
            Proposta para o cliente levar. Vira venda ou OS só quando ele aceitar.
          </p>
        </div>
        <Link to="/orcamentos/novo" className="btn-primary text-sm">
          <Plus size={14} /> Novo orçamento
        </Link>
      </div>

      {vencidos > 0 && (
        <div className="card card-body bg-amber-50 border-amber-200 flex items-center gap-3">
          <AlertTriangle className="text-amber-600 shrink-0" size={20} />
          <div className="flex-1 text-sm text-amber-900">
            <strong>{vencidos}</strong> orçamento(s) em aberto já passaram da validade.
            Renove a validade para poder converter.
          </div>
        </div>
      )}

      <div className="card card-body space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input className="input pl-9 !py-1.5 text-sm" placeholder="Buscar por cliente ou número..."
              value={busca} onChange={e => setBusca(e.target.value)}
              onKeyDown={enterAbrePrimeiro(filtrados, o => navigate(`/orcamentos/${o.id}`))} />
          </div>
          {['aberto', 'aceito', 'recusado', 'cancelado', ''].map(s => (
            <button key={s || 'todos'} onClick={() => setStatus(s)}
              className={`px-3 py-1 rounded text-xs ${status === s ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-700'}`}>
              {s === '' ? 'Todos' : STATUS_META[s].label}
            </button>
          ))}
          <span className="text-ink-300">|</span>
          {[['', 'Tudo'], ['venda', 'Venda'], ['servico', 'Serviço']].map(([v, l]) => (
            <button key={v || 'all'} onClick={() => setTipo(v)}
              className={`px-3 py-1 rounded text-xs ${tipo === v ? 'bg-ink-700 text-white' : 'bg-ink-100 text-ink-700'}`}>
              {l}
            </button>
          ))}
          <button onClick={() => setVerArquivados(v => !v)}
            className={`px-3 py-1 rounded text-xs flex items-center gap-1 ${
              verArquivados ? 'bg-ink-700 text-white' : 'bg-ink-100 text-ink-700'}`}>
            {verArquivados ? <ArchiveRestore size={12} /> : <Archive size={12} />}
            {verArquivados ? 'Vendo arquivados' : 'Arquivados'}
          </button>
          <span className="ml-auto text-xs text-ink-500">
            {filtrados.length} orçamento(s)
            {status === 'aberto' && <> · em aberto <strong className="text-ink-800">{formatMoney(totalAberto)}</strong></>}
          </span>
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Nº</th><th>Tipo</th><th>Cliente</th><th>Criado</th>
              <th>Validade</th><th className="text-right">Total</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="text-center py-8">Carregando...</td></tr>}
            {!isLoading && filtrados.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-ink-500">
                {orcamentos.length === 0
                  ? <>Nenhum orçamento por aqui ainda. <Link to="/orcamentos/novo" className="text-brand-700 underline">Criar o primeiro</Link>.</>
                  : 'Nenhum orçamento corresponde à busca.'}
              </td></tr>
            )}
            {filtrados.map(o => {
              const st = STATUS_META[o.status] || STATUS_META.aberto
              const Icon = st.icon
              return (
                <tr key={o.id} onClick={() => navigate(`/orcamentos/${o.id}`)}
                  className="hover:bg-violet-50 cursor-pointer">
                  <td className="font-mono font-semibold">#{o.numero}</td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      o.tipo === 'servico' ? 'bg-indigo-100 text-indigo-800' : 'bg-amber-100 text-amber-800'}`}>
                      {o.tipo === 'servico' ? 'Serviço' : 'Venda'}
                    </span>
                  </td>
                  <td className="truncate max-w-[260px]">{o.cliente_nome || '—'}</td>
                  <td className="text-xs text-ink-500">{formatDate(o.criado_em)}</td>
                  <td><SeloValidade o={o} /></td>
                  <td className="text-right font-semibold">{formatMoney(o.total)}</td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded inline-flex items-center gap-1 ${st.cor}`}>
                      <Icon size={11} /> {st.label}
                    </span>
                    {o.convertido_os_id && (
                      <div className="text-[10px] text-ink-500 mt-0.5">OS #{o.convertido_os_id}</div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}